import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

interface Listing {
  id: number;
  user_id: number;
  category: string;
  name: string;
  company_name: string | null;
  contact_number: string | null;
  city: string | null;
  address: string | null;
  fees_per_match: number | string | null;
  fees_per_day: number | string | null;
  experience_years: number | null;
  profile_picture: string | null;
  banner_url: string | null;
  metadata: Record<string, any> | null;
  is_published: boolean;
  approval_status: string;
  rejection_reason?: string | null;
  reviewed_by?: number | null;
  reviewed_at?: string | null;
  submitted_at?: string | null;
  created_at: string | null;
  user?: { id: number; phone_number: string | null; details?: { first_name?: string; last_name?: string } };
}

// The registry covers every community role; grounds are the one the panel had no
// way to review, so that is where this page opens.
const CATEGORIES = ['ground', 'box_cricket', 'academy', 'scorer', 'umpire', 'commentator', 'streamer', 'organizer'];
const STATUS_STYLE: Record<string, string> = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
};

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '-');
const label = (s: string) => s.replace(/_/g, ' ');

const CommunityListings: React.FC = () => {
  const [rows, setRows] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('ground');
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Listing | null>(null);
  // Unfiltered per-status totals from the API — a true queue size, independent of the
  // current filter or search.
  const [counts, setCounts] = useState<{ pending: number; approved: number; rejected: number }>({ pending: 0, approved: 0, rejected: 0 });
  // Reject needs a reason now (BE returns 400 without one), so it gets its own dialog.
  const [rejectTarget, setRejectTarget] = useState<Listing | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const rejectLen = rejectReason.trim().length;
  const rejectValid = rejectLen >= 10 && rejectLen <= 500;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (category) params.category = category;
      if (status) params.approval_status = status;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res: any = await api.get('/community-registry', { params });
      const payload = res.data || {};
      // The endpoint returns either a paginated envelope or a plain array.
      const list = Array.isArray(payload) ? payload : payload.data || [];
      setRows(list);
      setTotalPages(payload.pagination?.total_pages || 1);
      setTotal(payload.pagination?.total ?? list.length);
      if (payload.counts) {
        setCounts({
          pending: payload.counts.pending || 0,
          approved: payload.counts.approved || 0,
          rejected: payload.counts.rejected || 0,
        });
      }
    } catch (e) {
      toast.error('Failed to load community listings');
    } finally { setLoading(false); }
  }, [page, category, status, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const approve = async (row: Listing) => {
    if (!window.confirm(`Approve "${row.name}"?\n\nIt becomes visible in the app.`)) return;
    setBusyId(row.id);
    try {
      await api.put(`/community-registry/${row.id}/approve`);
      toast.success('Approved');
      setDetail(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to approve');
    } finally { setBusyId(null); }
  };

  const openReject = (row: Listing) => { setRejectReason(''); setRejectTarget(row); };

  const submitReject = async () => {
    if (!rejectTarget || !rejectValid) return;
    setRejectBusy(true);
    try {
      // Reason is required by the API (10–500 chars) and is shown to the owner in the app.
      await api.put(`/community-registry/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Rejected');
      setRejectTarget(null);
      setRejectReason('');
      setDetail(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to reject');
    } finally { setRejectBusy(false); }
  };

  const remove = async (row: Listing) => {
    if (!window.confirm(`Delete "${row.name}" permanently? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/community-registry/${row.id}`);
      toast.success('Deleted');
      setDetail(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to delete');
    } finally { setBusyId(null); }
  };

  const ownerName = (r: Listing) => {
    const d = r.user?.details;
    const n = d ? `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim() : '';
    return n || r.user?.phone_number || `#${r.user_id}`;
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Community Listings</h1>
          <p className="text-sm text-gray-500">
            Grounds, academies and services users register from the app's Community tab. Approve to make one visible.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, city, phone…" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-56" />
          <select value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{label(c)}</option>)}
          </select>
          <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            <option value="">Any status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={load} className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Refresh</button>
        </div>
      </div>

      {/* Queue totals — from the API, unaffected by the filter/search above. Tap to filter. */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(['pending', 'approved', 'rejected'] as const).map((s) => (
          <button key={s} onClick={() => { setPage(1); setStatus(s); }}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize border border-transparent ${STATUS_STYLE[s]} ${status === s ? 'ring-2 ring-offset-1 ring-gray-400' : 'opacity-80 hover:opacity-100'}`}>
            {s} · {counts[s]}
          </button>
        ))}
      </div>

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Listing</th>
                  <th className="text-left font-medium px-4 py-3">Category</th>
                  <th className="text-left font-medium px-4 py-3">City</th>
                  <th className="text-left font-medium px-4 py-3">Contact</th>
                  <th className="text-left font-medium px-4 py-3">Submitted by</th>
                  <th className="text-left font-medium px-4 py-3">Submitted</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Nothing here</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-900">
                      <button onClick={() => setDetail(r)} className="hover:underline text-left">
                        {r.name}
                      </button>
                      {r.company_name && <span className="block text-xs text-gray-400">{r.company_name}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{label(r.category)}</td>
                    <td className="px-4 py-3 text-gray-600">{r.city || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.contact_number || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{ownerName(r)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmt(r.submitted_at ?? r.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[r.approval_status] || 'bg-gray-100 text-gray-600'}`}>
                        {r.approval_status}
                      </span>
                      {!r.is_published && r.approval_status === 'approved' && (
                        <span className="block text-xs text-gray-400 mt-0.5">not published</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {r.approval_status !== 'approved' && (
                          <button onClick={() => approve(r)} disabled={busyId === r.id}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 text-green-700 hover:bg-green-50 disabled:opacity-50">
                            Approve
                          </button>
                        )}
                        {r.approval_status !== 'rejected' && (
                          <button onClick={() => openReject(r)} disabled={busyId === r.id}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                            Reject
                          </button>
                        )}
                        <button onClick={() => remove(r)} disabled={busyId === r.id}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Prev</button>
            <span className="text-gray-500">Page {page} of {totalPages}{total ? ` · ${total} listings` : ''}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {/* Detail */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-xl p-5 my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{detail.name}</h2>
                <p className="text-sm text-gray-500 capitalize">{label(detail.category)} · {detail.city || 'no city'}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            {detail.banner_url && (
              <img src={detail.banner_url} alt="" className="w-full h-36 object-cover rounded-lg mb-4" />
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-4">
              {[
                ['Company', detail.company_name],
                ['Contact', detail.contact_number],
                ['Address', detail.address],
                ['Fees / match', detail.fees_per_match],
                ['Fees / day', detail.fees_per_day],
                ['Experience', detail.experience_years != null ? `${detail.experience_years} years` : null],
                ['Submitted by', ownerName(detail)],
                ['Submitted', fmt(detail.submitted_at ?? detail.created_at)],
              ].map(([k, v]) => (
                <div key={String(k)}>
                  <dt className="text-xs text-gray-500">{k}</dt>
                  <dd className="text-gray-800">{v ? String(v) : '—'}</dd>
                </div>
              ))}
            </dl>

            {detail.metadata && Object.keys(detail.metadata).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-1">Details</div>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto text-gray-700">
                  {JSON.stringify(detail.metadata, null, 2)}
                </pre>
              </div>
            )}

            {detail.approval_status === 'rejected' && detail.rejection_reason && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="text-xs font-medium text-red-700 mb-1">Rejection reason (shown to the owner)</div>
                <p className="text-sm text-red-800 whitespace-pre-wrap">{detail.rejection_reason}</p>
              </div>
            )}
            {(detail.reviewed_at || detail.reviewed_by != null) && (
              <p className="text-xs text-gray-500 mb-4">
                Last decision{detail.reviewed_at ? ` on ${fmt(detail.reviewed_at)}` : ''}
                {detail.reviewed_by != null ? ` by admin #${detail.reviewed_by}` : ''}.
              </p>
            )}

            <p className="text-xs text-gray-400 mb-4">
              Community listings carry no coordinates — a ground listed here is separate from the
              Match Grounds entry a match is played at.
            </p>

            <div className="flex justify-end gap-2">
              <button onClick={() => remove(detail)} disabled={busyId === detail.id}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                Delete
              </button>
              {detail.approval_status !== 'rejected' && (
                <button onClick={() => openReject(detail)} disabled={busyId === detail.id}
                  className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                  Reject
                </button>
              )}
              {detail.approval_status !== 'approved' && (
                <button onClick={() => approve(detail)} disabled={busyId === detail.id}
                  className="text-sm font-medium px-5 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
                  Approve
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject — reason required (10–500 chars), shown to the owner in the app. */}
      {rejectTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Reject “{rejectTarget.name}”</h2>
            <p className="text-sm text-gray-500 mb-3">
              This is shown to the owner next to an <span className="font-medium">Edit &amp; resubmit</span> button —
              write it to them, telling them what to fix. Not an internal note.
            </p>
            <textarea autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              rows={4} maxLength={500}
              placeholder="e.g. The profile photo isn't of a person. Please upload a clear headshot."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
            <div className="flex items-center justify-between mt-1 mb-4">
              <span className={`text-xs ${rejectLen < 10 ? 'text-amber-600' : 'text-gray-400'}`}>
                {rejectLen < 10 ? `At least ${10 - rejectLen} more character${10 - rejectLen === 1 ? '' : 's'}` : 'Looks good'}
              </span>
              <span className="text-xs text-gray-400">{rejectLen}/500</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} disabled={rejectBusy}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={submitReject} disabled={rejectBusy || !rejectValid}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {rejectBusy ? 'Rejecting…' : 'Reject profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityListings;
